<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Feedback"
      subtitle="View submitted feedback history"
      icon="fas fa-star"
      gradient="orange"
    />

    <BRow class="py-2">
      <BCol
        cols="12"
        md="9"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableData.filter.search"
            placeholder="search comments, feedback type, or sender"
            autocomplete="off"
            debounce="600"
            type="text"
          />
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableData"
      :provider="tableProvider"
      :fields="tableData.fields"
      :filter="tableData.filter"
      :filter-on="tableData.filterOn"
      :sort-by="tableData.sortBy"
      :sort-desc="tableData.sortDesc"
      :sort-direction="tableData.sortDirection"
      :total-rows="tableData.totalRows"
      :current-page="tableData.currentPage"
      :per-page="tableData.perPage"
      :stacked="tableData.stacked"
      :busy="tableData.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="info"
            :disabled="tableData.busy"
            @click.prevent="showItem(item)"
          >
            View
          </BButton>
        </div>
      </template>

      <template #cell(student)="{ item }">
        {{ item.student?.id_number || item.feedbacker_name || 'N/A' }}
      </template>

      <template #cell(rating)="{ item }">
        <BBadge variant="warning">
          {{ item.rating || 0 }}/5
        </BBadge>
      </template>
    </CustomTable>

    <BModal
      ref="modalView"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      title="Feedback Details"
    >
      <BRow>
        <BCol
          cols="12"
          md="6"
        >
          <h6 class="font-weight-bolder">
            Feedback Target
          </h6>
          <p class="mb-2">
            {{ targetLabel(activeItem) }}
          </p>

          <h6 class="font-weight-bolder">
            Sender
          </h6>
          <p class="mb-2">
            {{ activeItem.student?.id_number || activeItem.feedbacker_name || 'N/A' }}
          </p>

          <h6 class="font-weight-bolder">
            Recipient
          </h6>
          <p class="mb-2">
            {{ recipientName(activeItem) }}
          </p>

          <h6 class="font-weight-bolder">
            Feedback Type
          </h6>
          <p class="mb-2 text-capitalize">
            {{ activeItem.feedback_type || 'N/A' }}
          </p>
        </BCol>

        <BCol
          cols="12"
          md="6"
        >
          <h6 class="font-weight-bolder">
            Rating
          </h6>
          <p class="mb-2">
            {{ activeItem.rating || 0 }}/5
          </p>

          <h6 class="font-weight-bolder">
            Date Submitted
          </h6>
          <p class="mb-2">
            {{ dateTimeShortFormatter(activeItem.date_submitted) || 'N/A' }}
          </p>

          <h6 class="font-weight-bolder">
            Email
          </h6>
          <p class="mb-2">
            {{ activeItem.feedbacker_email || activeItem.student?.email || 'N/A' }}
          </p>
        </BCol>

        <BCol cols="12">
          <h6 class="font-weight-bolder">
            Comment
          </h6>
          <p class="mb-0">
            {{ activeItem.comments || 'No comment provided.' }}
          </p>
        </BCol>
      </BRow>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="outline-dark"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Close
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import { SAdminFeedback } from '@/services'

export default {
  name: 'VAdminFeedback',

  components: { AdminPageHeader },

  mixins: [MxMisc, MxFormatter],

  middleware: ['auth', 'admin'],

  metaInfo: () => ({ title: 'Feedback' }),

  data () {
    return {
      activeItem: {},
      tableData: {
        busy: false,
        filter: {
          search: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'date_submitted',
        sortDesc: true,
        sortDirection: 'desc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'text-center w-md-100px' },
          { mobile: 1, key: 'student', label: 'Sender', class: 'min-w-100px' },
          { mobile: 2, key: 'feedback_type', class: 'min-w-150px text-capitalize' },
          { mobile: 3, key: 'rating', class: 'text-center min-w-100px' },
          { mobile: 4, key: 'date_submitted', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: mapGetters({
    currentUser: 'auth/user'
  }),

  methods: {
    recipientName (item) {
      const recipient = item?.recipient
      if (!recipient) return 'N/A'
      return `${recipient.first_name || ''} ${recipient.last_name || ''}`.trim() || recipient.username || `User #${recipient.id}`
    },

    targetLabel (item) {
      if (item?.appointment_id) return `Appointment #${item.appointment_id}`
      if (item?.counseling_session_id) return `Counseling Session #${item.counseling_session_id}`
      return 'N/A'
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableData.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        search: filter.search
      }

      return SAdminFeedback.getList(payload)
        .then(({ data: response }) => {
          const root = response?.data || response
          const data = root?.data || root
          this.tableData.totalRows = data?.total_rows || 0
          return data?.items || []
        })
        .catch(() => {
          this.tableData.totalRows = 0
          return []
        })
        .finally(() => {
          this.tableData.busy = false
        })
    },

    showItem (item) {
      this.activeItem = { ...item }
      this.$refs.modalView.show()
    }
  }
}
</script>
